import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';

import { PLATFORM_LEGAL_CORPORATION_ID } from '../api/constants';
import { APPINSIGHT_KEY } from '../api/constants';

const reactPlugin = new ReactPlugin();
const appInsights = new ApplicationInsights({
  config: {
    connectionString: `InstrumentationKey=${APPINSIGHT_KEY}`,
    extensions: [reactPlugin],
    enableAutoRouteTracking: true,
    disableAjaxTracking: false,
    autoTrackPageVisitTime: true,
    enableCorsCorrelation: false,
    enableRequestHeaderTracking: true,
    enableResponseHeaderTracking: true,
  },
});
appInsights.loadAppInsights();

interface AxiosErrorLike {
  message?: string;
  stack?: string;
  config?: {
    baseURL?: string;
    url?: string;
  };
  response?: {
    data?: {
      message?: string;
      stack_trace?: string;
    };
  };
}

const IGNORABLE_ERROR_PATTERNS = [
  'authentication exception',
  'User does not have an existing session',
  'GA4React intialization failed',
  'Request aborted',
];

const shouldIgnoreError = (messageText: string): boolean => {
  return IGNORABLE_ERROR_PATTERNS.some((pattern) => messageText.includes(pattern));
};

const determineInstanceFromSqlError = (errorMessage: string | undefined): string | null => {
  if (!errorMessage?.includes('SQLSTATE')) return null;

  const sqlMatch = errorMessage.match(/\[(-?\d+)\]/);
  const sqlCode = sqlMatch ? Number(sqlMatch[1]) : null;

  if (sqlCode === null) return 'PHP';
  return sqlCode >= 2000 ? 'MYSQL Down' : 'MYSQL';
};

const processResponseData = (
  data: { message?: string; stack_trace?: string },
  message: Record<string, string>,
): string => {
  let instance = determineInstanceFromSqlError(data?.message) ?? 'PHP';

  if (data?.stack_trace) {
    message['StackTrace'] = data.stack_trace;
  }
  if (data?.message) {
    message['Message'] = data.message;
  }
  if (message.Message.includes('Request failed with status code 502')) {
    instance = 'PHP DOWN';
  }

  return instance;
};

const trackException = (messageContext: string, error: unknown) => {
  let instance = 'REACT';
  const errorObj = error as AxiosErrorLike | undefined;
  const message: Record<string, string> = {
    Message: messageContext + ' - ' + (errorObj?.message ?? ''),
    LegalCorporationId: PLATFORM_LEGAL_CORPORATION_ID,
  };

  if (errorObj?.stack) {
    message['StackTrace'] = errorObj.stack;
  }
  if (errorObj?.message === 'Network Error') {
    instance = 'PHP Down';
  }

  if (errorObj?.config) {
    const config = errorObj.config;
    message['URL'] = (config?.baseURL ?? '') + (config?.url ?? '');
  }

  if (errorObj?.response?.data) {
    instance = processResponseData(errorObj.response.data, message);
  }

  if (shouldIgnoreError(message.Message)) {
    instance = '';
  }

  appInsights.addTelemetryInitializer((env: ITelemetryItem) => {
    env.tags = env.tags || [];
    env.tags['ai.cloud.role'] = 'Yellowstone';
    env.tags['ai.cloud.roleInstance'] = instance;
  });

  appInsights.trackException({ exception: errorObj as Error, properties: message });
};

export { appInsights, reactPlugin, trackException };
